import { UpdateLineItemErrorCodes } from '@app/types/monolith/types';

type UserError = { code: string | number; field: string[] };

import { defineMessages } from '@redbubble/next/intl';
import { addDDError } from './utils/datadog';

export class ErrorCodeValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ErrorCodeValidationError';
  }
}

export const userErrorMessages = defineMessages({
  lineItemQtyNotAvailable: {
    defaultMessage: 'Limit of {max_quantity} items reached',
  },
  lineItemUnhandledUpdateError: {
    defaultMessage: `Sorry, due to a system error we couldn't update the quantity of an item below. Please try again.`,
  },
  couponCodeInvalidError: {
    defaultMessage: `Oops! That’s not a valid coupon code.`,
  },
  couponCodeExpiredError: {
    defaultMessage: `Oops! Your coupon has expired`,
  },
  couponCodeLoginToUse: {
    defaultMessage: 'Oops! You need to login to use this coupon',
  },
  couponCodeNotAppliedToYourAccount: {
    defaultMessage: `Oops! This coupon doesn't belong to your account`,
  },
  couponCodeSpendThreshold: {
    defaultMessage: `Your cart needs to be over {threshold} to apply this coupon`,
  },
  couponCodeArtistThreshold: {
    defaultMessage: `Cannot apply coupon to cart with items from more than one artist`,
  },
});

export const serverErrorMessage = defineMessages({
  updateLineItemServerError: {
    defaultMessage: `Sorry, due to a system error we couldn't update the quantity of an item below. Please try again.`,
  },
  couponApplyServerError: {
    defaultMessage: `Sorry, we couldn't apply this coupon, please try again.`,
  },
  couponRemoveServerError: {
    defaultMessage: `Oops! We couldn't remove the coupon. Please refresh the page and try again.`,
  },
  shippingPreferenceServerError: {
    defaultMessage:
      'Sorry, something went wrong while updating your shipping preference. Please try again.',
  },
});

const errorCodeDict = {
  'cart-lineItem:1000': userErrorMessages.lineItemUnhandledUpdateError,
  'cart-lineItem:1100': userErrorMessages.lineItemUnhandledUpdateError,
  'cart-lineItem:1200': userErrorMessages.lineItemUnhandledUpdateError,
  'cart-lineItem-quantity:1100': userErrorMessages.lineItemQtyNotAvailable,
  'cart-coupon:invalid': userErrorMessages.couponCodeInvalidError,
  'cart-coupon:expired': userErrorMessages.couponCodeExpiredError,
  'cart-coupon:spend_threshold': userErrorMessages.couponCodeSpendThreshold,
  'cart-coupon:artist_threshold': userErrorMessages.couponCodeArtistThreshold,
  'cart-coupon:login_to_use': userErrorMessages.couponCodeLoginToUse,
  'cart-coupon:not_applied_to_account':
    userErrorMessages.couponCodeNotAppliedToYourAccount,
  [UpdateLineItemErrorCodes.QuantityInvalid]:
    userErrorMessages.lineItemQtyNotAvailable,
  [UpdateLineItemErrorCodes.ItemNotFound]:
    userErrorMessages.lineItemUnhandledUpdateError,
  [UpdateLineItemErrorCodes.UnexpectedError]:
    userErrorMessages.lineItemUnhandledUpdateError,
  [UpdateLineItemErrorCodes.InvalidInput]:
    userErrorMessages.lineItemUnhandledUpdateError,
};

/**
 * Fetches an error copy from a code.
 *
 * The caller is responsible for substituting any parameters in the copy.
 * Used in conjunction with Monolith and other APIs that return error codes.
 */
export const getErrorCopyFromCode = ({ code }: { code: string }) =>
  errorCopyOrThrow({ key: code });

/**
 * Fetches an error copy from a code and field identifiers.
 *
 * The caller is responsible for substituting any parameters in the copy.
 * Should only be used in conjunction with cart BFF schema.
 * For Monolith (and other systems), use `getErrorCopyFromCode`.
 */
export const getUserErrorMessage = ({ code, field }: UserError) => {
  // From: code: 1200 | field: ["cart", "lineItem"]
  // To: "cart-lineItem:1200"
  const key = `${field.join('-')}:${code}`;

  return errorCopyOrThrow({ key });
};

const errorCopyOrThrow = ({
  key,
}: {
  key: string;
}): { defaultMessage: string } => {
  if (errorCodeDict[key]) {
    return errorCodeDict[key];
  }

  const validationError = new ErrorCodeValidationError(
    `No error copy found for provided key ${key}`
  );

  addDDError(validationError);

  throw validationError;
};
